import * as React from 'react';
import { connect } from 'redux-scaffolding-ts';
import { nameof } from '../../utils/object';
import { ItemReference } from '../../stores/dataStore';
import { isNullOrWhiteSpaces } from '../../utils/useful-functions';
import { ProfessionsStore, ProfessionDto } from 'stores/configuration/profiles/professions-store';
import { useCallback, useEffect, useState } from 'react';
import TailwindSelect from 'widgets/editors/tailwind-select';
import { useTranslation } from 'react-i18next';

interface TailwindProfessionEditorProps {
  value: ItemReference;
  onChange?: (value: ItemReference) => void;
  className?: string;
  placeholder?: string;
  professions?: ProfessionsStore;
  nullable?: boolean;
  readOnly?: boolean;
  clearable?: boolean;
  onBlur?: (event: React.FocusEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;
}

const TailwindProfessionEditor: React.FC<TailwindProfessionEditorProps> = ({
  value,
  onChange,
  className = '',
  placeholder,
  professions,
  nullable = false,
  readOnly = false,
  clearable = true,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState<ItemReference>(value);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);

  const getProfessionMethod = useCallback(
    async (search: string = '') => {
      try {
        setIsLoading(true);
        const result = await professions.getAllAsync(
          {
            searchQuery: '',
            skip: 0,
            take: 100000,
            orderBy: [{ direction: 'Ascending', field: nameof<ProfessionDto>('profession'), useProfile: false }],
            filter: isNullOrWhiteSpaces(search)
              ? []
              : [`startswith(tolower(${nameof<ProfessionDto>('profession')}), '${search.toLowerCase()}')`]
          },
          search
        );
        setOptions(
          result.items.map(item => ({
            value: item.id,
            label: item.profession
          }))
        );
      } catch (error) {
        console.error('Error loading locations:', error);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [professions]
  );

  useEffect(() => {
    getProfessionMethod();
  }, [getProfessionMethod]);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleChange = (newValue: string | null) => {
    const selected = newValue ? options.find(opt => opt.value === newValue) : null;

    const itemRef = selected ? { id: selected.value, title: selected.label } : null;

    setSelectedValue(itemRef);
    onChange?.(itemRef);
  };

  return (
    <TailwindSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(delivery => ({
              id: delivery.value,
              text: delivery.label
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={selectedValue?.id || ''}
      onChange={handleChange}
      placeholder={placeholder || 'Select an option'}
      clearable
      searchable={!isLoading}
      className="w-full"
      disabled={readOnly}
    />
  );
};

// Wire up the React component to the Redux store
export default connect(['professions', ProfessionsStore])(TailwindProfessionEditor);
